/**
 * 我的订阅页面 - 移动端优化版
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { subscriptionsAPI, type Subscription } from '@/services/subscriptionsApi';
import { useAuthStore } from '@/stores/auth';
import { Loader2, Package, Clock, TrendingUp, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function MySubscriptionsPage() {
  const { accessToken } = useAuthStore();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    if (!accessToken) return;

    setLoading(true);
    try {
      const response = await subscriptionsAPI.getMySubscriptions(accessToken);
      console.log('订阅列表响应:', response);
      
      if (response.success && response.data) {
        const subscriptionsData = (response.data as any).data || response.data;
        
        if (Array.isArray(subscriptionsData)) {
          setSubscriptions(subscriptionsData);
        } else {
          console.warn('订阅数据格式不正确:', subscriptionsData);
          setSubscriptions([]);
        }
      } else {
        setSubscriptions([]);
      }
    } catch (error: any) {
      console.error('加载订阅失败:', error);
      toast.error('加载订阅失败: ' + error.message);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (subscriptionId: string, currentEnabled: boolean) => {
    if (!accessToken) return;

    setActionLoading(subscriptionId);
    try {
      const response = await subscriptionsAPI.toggleSubscription(
        subscriptionId, 
        !currentEnabled, 
        accessToken
      );
      if (response.success) {
        toast.success(currentEnabled ? '已禁用' : '已启用');
        // 🆕 只更新单个订阅状态，不刷新整页
        setSubscriptions(prevSubs => prevSubs.map((item: any) => {
          const sub = item.subscription || item;
          if (sub.id === subscriptionId) {
            return {
              ...item,
              subscription: { ...sub, enabled: !currentEnabled }
            };
          }
          return item;
        }));
      }
    } catch (error: any) {
      toast.error('操作失败: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnsubscribe = async (toolId: string) => {
    if (!accessToken) return;

    setActionLoading(toolId);
    try {
      const response = await subscriptionsAPI.unsubscribe(toolId, accessToken);
      if (response.success) {
        toast.success('已取消订阅');
        setDeleteConfirm(null);
        // 🆕 只移除对应的订阅，不刷新整页
        setSubscriptions(prevSubs => prevSubs.filter((item: any) => {
          const sub = item.subscription || item;
          return sub.tool_id !== toolId;
        }));
      }
    } catch (error: any) {
      toast.error('取消订阅失败: ' + error.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <LayoutWithSidebar title="我的订阅">
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar title="我的订阅">
      <div className="container mx-auto py-4 px-3 sm:py-8 sm:px-4 max-w-7xl">
        {/* 标题区域 - 移动端优化 */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">我的订阅</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            管理你订阅的工具
          </p>
        </div>

      {subscriptions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 sm:py-12">
            <Package className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-4" />
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 text-center px-4">
              你还没有订阅任何工具
            </p>
            <Button 
              onClick={() => window.location.href = '/tools'}
              className="h-10 sm:h-auto text-sm sm:text-base"
            >
              浏览工具市场
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {subscriptions.map((item: any) => {
            const sub = item.subscription || item;
            const tool = item.tool;
            const server = item.server;
            
            const isLoading = actionLoading === sub.id;
            const toolName = tool?.display_name || tool?.name || '未知工具';
            const serverName = server?.name || '未知服务器';
            const isPublished = tool?.is_published !== false;
            const isOffline = !isPublished;

            return (
              <Card 
                key={sub.id} 
                className={`${isOffline ? 'opacity-75 border-orange-200' : ''} hover:shadow-lg transition-shadow`}
              >
                <CardHeader className="pb-3 sm:pb-6">
                  <div className="flex items-start justify-between gap-2">
                    <Package className={`h-6 w-6 sm:h-8 sm:w-8 mb-2 flex-shrink-0 ${isOffline ? 'text-gray-400' : 'text-blue-500'}`} />
                    <div className="flex flex-col gap-2 items-end">
                      {isOffline ? (
                        <Badge variant="destructive" className="bg-orange-500 text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          已下架
                        </Badge>
                      ) : (
                        <Badge variant={sub.enabled ? 'default' : 'secondary'} className="text-xs">
                          {sub.enabled ? '已启用' : '已禁用'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardTitle className={`text-lg sm:text-xl break-words ${isOffline ? 'text-gray-500' : ''}`}>
                    {toolName}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {isOffline && (
                      <div className="mb-2 text-orange-600 text-xs sm:text-sm font-medium flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span>此工具已被管理员下架，暂时无法使用</span>
                      </div>
                    )}
                    <p className="line-clamp-2">{tool?.description || '无描述'}</p>
                    <div className="mt-2 text-xs text-gray-500 break-words">
                      服务器: {serverName}
                      {tool?.category && <span className="ml-2">| 分类: {tool.category}</span>}
                    </div>
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3 sm:space-y-4 py-3 sm:py-4">
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>订阅于 {new Date(sub.subscribed_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <TrendingUp className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>使用次数: {sub.usage_count || 0}</span>
                    </div>
                    {sub.last_used_at && (
                      <div className="flex items-center text-gray-600 dark:text-gray-400 text-xs">
                        最后使用: {new Date(sub.last_used_at).toLocaleString()}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-3 sm:pt-4 border-t">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={sub.enabled && !isOffline}
                        onCheckedChange={() => handleToggle(sub.id, sub.enabled)}
                        disabled={isLoading || isOffline}
                      />
                      <span className={`text-xs sm:text-sm ${isOffline ? 'text-gray-400' : ''}`}>
                        {isOffline ? '已禁用' : '启用'}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(sub.tool_id)}
                      disabled={isLoading}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* 删除确认对话框 - 移动端优化 */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg sm:text-xl">确认取消订阅</AlertDialogTitle>
            <AlertDialogDescription className="text-sm sm:text-base">
              此操作将取消订阅该工具，你将无法继续使用它。确定要继续吗？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleUnsubscribe(deleteConfirm)}
              className="w-full sm:w-auto bg-red-500 hover:bg-red-600"
            >
              确认取消订阅
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </LayoutWithSidebar>
  );
}
